/**
 * StoreSettings - Merchant Store Customization Page
 * Upload logo, banner, select theme/colors, manage featured products, QR code generator
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';
import StoreQRCode from '../components/StoreQRCode';
import {
  ArrowLeft,
  Store,
  Upload,
  Palette,
  Image,
  FileText,
  Star,
  Save,
  Loader2,
  Check,
  Eye,
  Trash2,
  Plus,
  QrCode,
  Navigation,
  MapPin as MapPinIcon,
  Droplets,
  ArrowRight,
} from 'lucide-react';

// Theme options - 15+ colors including bright options
const THEMES = [
  // Jamaica Colors
  { id: 'green', name: 'Jamaica Green', color: '#009B3A', gradient: 'from-[#009B3A]/20 via-slate-900 to-slate-950' },
  { id: 'gold', name: 'Jamaica Gold', color: '#FCD116', gradient: 'from-[#FCD116]/20 via-slate-900 to-slate-950' },
  { id: 'black', name: 'Jamaica Black', color: '#000000', gradient: 'from-slate-800 via-slate-900 to-slate-950' },
  // Bright Colors
  { id: 'red', name: 'Vibrant Red', color: '#FF3B30', gradient: 'from-[#FF3B30]/20 via-slate-900 to-slate-950' },
  { id: 'orange', name: 'Sunset Orange', color: '#FF9500', gradient: 'from-[#FF9500]/20 via-slate-900 to-slate-950' },
  { id: 'yellow', name: 'Bright Yellow', color: '#FFCC00', gradient: 'from-[#FFCC00]/20 via-slate-900 to-slate-950' },
  { id: 'lime', name: 'Lime Green', color: '#32CD32', gradient: 'from-[#32CD32]/20 via-slate-900 to-slate-950' },
  { id: 'teal', name: 'Teal', color: '#00CED1', gradient: 'from-[#00CED1]/20 via-slate-900 to-slate-950' },
  { id: 'cyan', name: 'Cyan', color: '#00BFFF', gradient: 'from-[#00BFFF]/20 via-slate-900 to-slate-950' },
  { id: 'blue', name: 'Ocean Blue', color: '#007AFF', gradient: 'from-[#007AFF]/20 via-slate-900 to-slate-950' },
  { id: 'purple', name: 'Royal Purple', color: '#AF52DE', gradient: 'from-[#AF52DE]/20 via-slate-900 to-slate-950' },
  { id: 'pink', name: 'Hot Pink', color: '#FF2D92', gradient: 'from-[#FF2D92]/20 via-slate-900 to-slate-950' },
  { id: 'coral', name: 'Coral', color: '#FF6B6B', gradient: 'from-[#FF6B6B]/20 via-slate-900 to-slate-950' },
  { id: 'mint', name: 'Mint Fresh', color: '#00D9A5', gradient: 'from-[#00D9A5]/20 via-slate-900 to-slate-950' },
  { id: 'white', name: 'Clean White', color: '#FFFFFF', gradient: 'from-slate-700 via-slate-800 to-slate-900' },
];

export default function StoreSettings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [business, setBusiness] = useState(null);
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [showQRCode, setShowQRCode] = useState(false);
  
  // Form state
  const [storeName, setStoreName] = useState('');
  const [about, setAbout] = useState('');
  const [policies, setPolicies] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('green');
  const [reviewsEnabled, setReviewsEnabled] = useState(true);
  const [featuredProductIds, setFeaturedProductIds] = useState([]);
  const [customColor, setCustomColor] = useState('#009B3A');
  const [useCustomColor, setUseCustomColor] = useState(false);
  
  // File uploads
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [storeLatitude, setStoreLatitude] = useState(null);
  const [storeLongitude, setStoreLongitude] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // Get business info
      const businessRes = await api.get('/business/me');
      setBusiness(businessRes.data);
      
      // Get store settings
      const storeRes = await api.get(`/stores/${businessRes.data.id}`);
      const storeData = storeRes.data.store;
      setStore(storeData);
      
      // Populate form
      setStoreName(storeData?.store_name || businessRes.data.business_name || '');
      setAbout(storeData?.about || '');
      setPolicies(storeData?.policies || '');
      setSelectedTheme(storeData?.store_theme || 'green');
      if (storeData?.store_theme === 'custom' && storeData?.custom_theme_color) {
        setUseCustomColor(true);
        setCustomColor(storeData.custom_theme_color);
      }
      setReviewsEnabled(storeData?.reviews_enabled !== false);
      setFeaturedProductIds(storeData?.featured_product_ids || []);
      setLogoPreview(storeData?.store_logo || businessRes.data.logo);
      setBannerPreview(storeData?.store_banner);
      
      // Get merchant products for featured selection
      const productsRes = await api.get('/merchant/products');
      setProducts(productsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 404) {
        toast.error('Please register a business first');
        navigate('/seller/apply');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude.toFixed(6);
        const lng = pos.coords.longitude.toFixed(6);
        setStoreLatitude(lat);
        setStoreLongitude(lng);
        try {
          await api.put('/stores/my-store/location', { latitude: parseFloat(lat), longitude: parseFloat(lng) });
          toast.success("Location coordinates saved!");
        } catch (e) { toast.success("Coordinates captured! Save your settings to apply."); }
        setGettingLocation(false);
      },
      () => { toast.error("Failed to get location"); setGettingLocation(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === 'logo') {
        setLogoFile(file);
        setLogoPreview(reader.result);
      } else {
        setBannerFile(file);
        setBannerPreview(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const uploadFile = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await api.post(`/applications/upload-document?doc_type=store_${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data.file_url;
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      throw error;
    }
  };

  const handleSave = async () => {
    if (!business?.id) {
      toast.error('No business found');
      return;
    }
    
    setSaving(true);
    setUploading(logoFile || bannerFile);
    
    try {
      let logoUrl = store?.store_logo;
      let bannerUrl = store?.store_banner;
      
      // Upload new files if selected
      if (logoFile) {
        logoUrl = await uploadFile(logoFile, 'logo');
      }
      if (bannerFile) {
        bannerUrl = await uploadFile(bannerFile, 'banner');
      }
      
      setUploading(false);
      
      // Update store settings
      await api.put(`/stores/${business.id}`, {
        store_name: storeName,
        store_logo: logoUrl,
        store_banner: bannerUrl,
        store_theme: useCustomColor ? 'custom' : selectedTheme,
        custom_theme_color: useCustomColor ? customColor : null,
        about,
        policies,
        reviews_enabled: reviewsEnabled,
        featured_product_ids: featuredProductIds
      });
      
      toast.success('Store settings saved!');
      setLogoFile(null);
      setBannerFile(null);
      fetchData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const toggleFeaturedProduct = (productId) => {
    setFeaturedProductIds(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      }
      if (prev.length >= 5) {
        toast.error('Maximum 5 featured products');
        return prev;
      }
      return [...prev, productId];
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00A86B] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading store settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/business')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold flex items-center gap-2">
                  <Store className="w-5 h-5 text-[#00A86B]" />
                  Store Settings
                </h1>
                <p className="text-sm text-slate-400">Customize your store appearance</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/store/${business?.id}`)}
                className="border-slate-700"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#00A86B] hover:bg-[#00A86B]/90"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {uploading ? 'Uploading...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Share Your Store Link */}
        {business?.id && (
          <Card className="p-6 bg-gradient-to-r from-[#00A86B]/10 to-[#FFD300]/10 border-[#00A86B]/30">
            <h3 className="font-semibold mb-3 flex items-center gap-2 text-white">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#00A86B]"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" x2="12" y1="2" y2="15"/></svg>
              Share Your Store
            </h3>
            <p className="text-sm text-slate-400 mb-4">Copy your store link and share it on social media to attract customers!</p>
            <div className="flex items-center gap-2 p-3 bg-slate-900 rounded-lg border border-slate-700 mb-4">
              <span className="text-sm font-mono text-[#00A86B] truncate flex-1">
                https://liantiaoja.com/store/{business.id}
              </span>
              <Button 
                size="sm" 
                variant="outline"
                className="border-[#00A86B] text-[#00A86B] hover:bg-[#00A86B]/10"
                onClick={() => {
                  navigator.clipboard.writeText(`https://liantiaoja.com/store/${business.id}`);
                  toast.success('Store link copied to clipboard!');
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                Copy
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button 
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Check out my store on Liantiao! 🛒🇯🇲\nhttps://liantiaoja.com/store/${business.id}`)}`, '_blank')}
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 mr-1" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Share on WhatsApp
              </Button>
              <Button 
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://liantiaoja.com/store/${business.id}`)}`, '_blank', 'width=600,height=400')}
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 mr-1" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Share on Facebook
              </Button>
              <Button 
                size="sm"
                variant="outline"
                className="border-[#FFD300] text-[#FFD300] hover:bg-[#FFD300]/10"
                onClick={() => setShowQRCode(!showQRCode)}
                data-testid="toggle-qr-btn"
              >
                <QrCode className="w-4 h-4 mr-1" />
                {showQRCode ? 'Hide QR Code' : 'Get QR Code'}
              </Button>
              <Button 
                size="sm"
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
                onClick={() => navigate(`/store/${business.id}`)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" x2="21" y1="14" y2="3"/></svg>
                View My Store
              </Button>
            </div>
          </Card>
        )}

        {/* QR Code Section - Collapsible */}
        {showQRCode && business?.id && (
          <StoreQRCode 
            storeId={business.id}
            storeName={storeName || business.business_name}
            storeSlug={store?.store_slug}
            className="bg-slate-800/50 border-slate-700"
          />
        )}

        {/* Store Name */}
        <Card className="p-6 bg-slate-800/50 border-slate-700">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Store className="w-5 h-5 text-[#00A86B]" />
            Store Name
          </h3>
          <Input
            value={storeName}
            onChange={(e) => setStoreName(e.target.value)}
            placeholder="Your Store Name"
            className="bg-slate-900 border-slate-700"
            data-testid="store-name-input"
          />
        </Card>

        {/* Store Location */}
        <Card className="p-6 bg-slate-800/50 border-slate-700">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <MapPinIcon className="w-5 h-5 text-[#00A86B]" />
            Store Location
          </h3>
          <p className="text-sm text-slate-400 mb-3">Capture your store's GPS coordinates so customers can find you on the Liantiao app.</p>
          <div className="flex items-center gap-4">
            {storeLatitude && storeLongitude ? (
              <div className="flex-1 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                <p className="text-emerald-400 text-sm font-medium">Location Captured</p>
                <p className="text-slate-400 text-xs font-mono">{storeLatitude}, {storeLongitude}</p>
              </div>
            ) : (
              <div className="flex-1 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-amber-400 text-sm">No location set. Capture your coordinates.</p>
              </div>
            )}
            <Button onClick={getCurrentLocation} disabled={gettingLocation} variant="outline" className="border-slate-700" data-testid="capture-location-btn">
              {gettingLocation ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Getting...</> : <><Navigation className="w-4 h-4 mr-2" />Capture Location</>}
            </Button>
          </div>
        </Card>

        {/* Logo Upload */}
        <Card className="p-6 bg-slate-800/50 border-slate-700">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Image className="w-5 h-5 text-[#00A86B]" />
            Store Logo
          </h3>
          <div className="flex items-start gap-6">
            <div className="w-32 h-32 rounded-xl bg-slate-900 border-2 border-dashed border-slate-700 flex items-center justify-center overflow-hidden">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <Store className="w-12 h-12 text-slate-600" />
              )}
            </div>
            <div className="flex-1">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileSelect(e, 'logo')}
                className="hidden"
                id="logo-upload"
              />
              <label htmlFor="logo-upload">
                <Button variant="outline" className="border-slate-700 cursor-pointer" asChild>
                  <span>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Logo
                  </span>
                </Button>
              </label>
              <p className="text-sm text-slate-400 mt-2">
                Recommended: 400x400px, PNG or JPG (max 5MB)
              </p>
              {logoFile && (
                <p className="text-sm text-[#00A86B] mt-1 flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  New logo selected
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* Banner Upload */}
        <Card className="p-6 bg-slate-800/50 border-slate-700">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Image className="w-5 h-5 text-[#00A86B]" />
            Store Banner
          </h3>
          <div 
            className="w-full h-48 rounded-xl bg-slate-900 border-2 border-dashed border-slate-700 flex items-center justify-center overflow-hidden mb-4"
          >
            {bannerPreview ? (
              <img src={bannerPreview} alt="Banner" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center text-slate-600">
                <Image className="w-12 h-12 mx-auto mb-2" />
                <p>No banner uploaded</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileSelect(e, 'banner')}
              className="hidden"
              id="banner-upload"
            />
            <label htmlFor="banner-upload">
              <Button variant="outline" className="border-slate-700 cursor-pointer" asChild>
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Banner
                </span>
              </Button>
            </label>
            {bannerPreview && (
              <Button 
                variant="ghost" 
                className="text-red-400"
                onClick={() => {
                  setBannerFile(null);
                  setBannerPreview(null);
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove
              </Button>
            )}
          </div>
          <p className="text-sm text-slate-400 mt-2">
            Recommended: 1200x400px, PNG or JPG (max 5MB)
          </p>
        </Card>

        {/* Theme Selection */}
        <Card className="p-6 bg-slate-800/50 border-slate-700">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Palette className="w-5 h-5 text-[#00A86B]" />
            Store Theme Color
          </h3>
          <p className="text-sm text-slate-400 mb-4">Choose a theme color for your store. Changes apply immediately when saved.</p>
          
          {/* Preset Colors Grid */}
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-5 lg:grid-cols-5 gap-3 mb-6">
            {THEMES.map((theme) => (
              <button
                key={theme.id}
                onClick={() => { setSelectedTheme(theme.id); setUseCustomColor(false); }}
                className={`p-3 rounded-xl border-2 transition-all ${
                  selectedTheme === theme.id && !useCustomColor
                    ? 'border-[#00A86B] bg-[#00A86B]/10 ring-2 ring-[#00A86B]/30'
                    : 'border-slate-700 hover:border-slate-500'
                }`}
                data-testid={`theme-${theme.id}`}
              >
                <div 
                  className="w-full aspect-square rounded-lg mb-2 border border-slate-600"
                  style={{ backgroundColor: theme.color }}
                />
                <div className="flex items-center justify-center gap-1">
                  <span className="text-xs font-medium truncate">{theme.name}</span>
                  {selectedTheme === theme.id && !useCustomColor && (
                    <Check className="w-3 h-3 text-[#00A86B] flex-shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Custom Color Picker */}
          <div className="border-t border-slate-700 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="useCustomColor"
                  checked={useCustomColor}
                  onChange={(e) => setUseCustomColor(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-[#00A86B] focus:ring-[#00A86B]"
                />
                <label htmlFor="useCustomColor" className="text-sm font-medium">Use Custom Color</label>
              </div>
              {useCustomColor && (
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={customColor}
                    onChange={(e) => setCustomColor(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border-2 border-slate-600"
                  />
                  <input
                    type="text"
                    value={customColor}
                    onChange={(e) => setCustomColor(e.target.value)}
                    placeholder="#009B3A"
                    className="w-24 px-2 py-1 text-sm bg-slate-900 border border-slate-600 rounded"
                  />
                </div>
              )}
            </div>
            {useCustomColor && (
              <div 
                className="w-full h-16 rounded-lg border border-slate-600"
                style={{ backgroundColor: customColor }}
              />
            )}
          </div>
        </Card>


        {/* Watermark Settings Link */}
        <Card className="p-6 bg-gradient-to-r from-[#009B3A]/10 to-[#FCD116]/10 border-[#009B3A]/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#009B3A]/20 rounded-lg">
                <Droplets className="w-5 h-5 text-[#009B3A]" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Product Watermarks</h3>
                <p className="text-sm text-slate-400">Protect your product images with your logo</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/seller/watermark')}
              className="bg-[#009B3A] hover:bg-[#008030]"
            >
              Manage Watermarks
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Card>

        {/* About Section */}
        <Card className="p-6 bg-slate-800/50 border-slate-700">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#00A86B]" />
            About Your Store
          </h3>
          <Textarea
            value={about}
            onChange={(e) => setAbout(e.target.value)}
            placeholder="Tell customers about your business, what you sell, your story..."
            className="bg-slate-900 border-slate-700 min-h-[120px]"
            data-testid="store-about-input"
          />
        </Card>

        {/* Store Policies */}
        <Card className="p-6 bg-slate-800/50 border-slate-700">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#00A86B]" />
            Store Policies
          </h3>
          <Textarea
            value={policies}
            onChange={(e) => setPolicies(e.target.value)}
            placeholder="Return policy, shipping info, payment terms..."
            className="bg-slate-900 border-slate-700 min-h-[120px]"
            data-testid="store-policies-input"
          />
        </Card>

        {/* Reviews Toggle */}
        <Card className="p-6 bg-slate-800/50 border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold flex items-center gap-2">
                <Star className="w-5 h-5 text-[#FFD300]" />
                Customer Reviews
              </h3>
              <p className="text-sm text-slate-400 mt-1">
                Allow customers to leave reviews on your store
              </p>
            </div>
            <button
              onClick={() => setReviewsEnabled(!reviewsEnabled)}
              className={`w-14 h-8 rounded-full transition-all ${
                reviewsEnabled ? 'bg-[#00A86B]' : 'bg-slate-700'
              }`}
              data-testid="reviews-toggle"
            >
              <div className={`w-6 h-6 bg-white rounded-full transition-transform ${
                reviewsEnabled ? 'translate-x-7' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </Card>

        {/* Store Preview - How Shoppers See Your Store */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-400" />
              How Shoppers See Your Store
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border border-slate-700 rounded-lg overflow-hidden bg-slate-900">
              {/* Banner */}
              <div className="h-32 bg-gradient-to-r from-slate-800 to-slate-700 relative">
                {store?.store_banner ? (
                  <img src={store.store_banner} alt="Banner" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm">No banner uploaded</div>
                )}
              </div>
              {/* Store info */}
              <div className="p-4 flex items-center gap-4 -mt-8 relative z-10">
                <div className="w-16 h-16 rounded-full bg-slate-800 border-2 border-slate-700 overflow-hidden flex-shrink-0">
                  {store?.store_logo ? (
                    <img src={store.store_logo} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-500 text-xs">Logo</div>
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">{store?.store_name || storeName || 'Your Store'}</h3>
                  <p className="text-slate-400 text-sm">{products?.length || 0} products</p>
                </div>
              </div>
              {/* Products grid preview */}
              <div className="px-4 pb-4 grid grid-cols-3 gap-2">
                {(products || []).slice(0, 3).map((p, i) => (
                  <div key={i} className="bg-slate-800 rounded overflow-hidden">
                    <div className="h-20 bg-slate-700">
                      {p.images?.[0] ? (
                        <img src={p.images[0]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-600 text-xs">No img</div>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-xs text-white truncate">{p.title}</p>
                      <p className="text-xs text-emerald-400 font-bold">J${p.price?.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
                {(!products || products.length === 0) && (
                  <div className="col-span-3 text-center py-4 text-slate-500 text-sm">No products yet - add some to see how your store looks!</div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Featured Products */}
        <Card className="p-6 bg-slate-800/50 border-slate-700">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-[#FFD300]" />
            Featured Products
            <span className="text-sm font-normal text-slate-400">
              ({featuredProductIds.length}/5)
            </span>
          </h3>
          
          {products.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Store className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No products yet</p>
              <Button
                variant="outline"
                className="mt-4 border-slate-700"
                onClick={() => navigate('/merchant/products/new')}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Products
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {products.map((product) => (
                <button
                  key={product.id}
                  onClick={() => toggleFeaturedProduct(product.id)}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    featuredProductIds.includes(product.id)
                      ? 'border-[#FFD300] bg-[#FFD300]/10'
                      : 'border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <div className="w-full h-20 rounded-lg bg-slate-900 mb-2 overflow-hidden">
                    {product.images?.[0] ? (
                      <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-600">
                        <Image className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <p className="text-sm font-medium truncate">{product.title}</p>
                  <p className="text-xs text-[#00A86B]">J${product.price?.toLocaleString()}</p>
                  {featuredProductIds.includes(product.id) && (
                    <div className="flex items-center gap-1 text-[#FFD300] text-xs mt-1">
                      <Star className="w-3 h-3 fill-current" />
                      Featured
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
